import { Transition } from "@headlessui/react"
import classnames from "classnames"
import PropTypes from "prop-types"
import React, { Fragment, useCallback, useContext } from "react"

import Button from "../../../components/Button"
import { SignContractWizardContext } from "./SignContractWizardContext"
import StepNumber from "./StepNumber"

const SignContractWizardNavigation = ({ isSubmitting }) => {
  const { agreementOverview, dispatch, state, steps, wizardCopy } = useContext(
    SignContractWizardContext
  )
  const { currentStep, declined, expired } = state
  const declinedOrExpired = declined || expired

  const isNavigable = useCallback(
    (stepNumber) => {
      if (isSubmitting) return false
      if (declinedOrExpired) return false
      if (stepNumber === 1) return true
      return steps
        .slice(0, stepNumber - 1)
        .every((step) => step.validate(state))
    },
    [isSubmitting, declinedOrExpired, steps, state]
  )

  const handleClickStep = (stepNumber) => (event) => {
    event.preventDefault()

    if (isNavigable(stepNumber)) {
      dispatch({ type: "SET_CURRENT_STEP", payload: stepNumber })
    }
  }

  const handleBack = () => {
    dispatch({ type: "GO_TO_PREVIOUS_STEP" })
  }

  if (agreementOverview) {
    const stepperSteps = steps.slice(1)
    const totalSteps = stepperSteps.length
    const displayCurrent = currentStep - 1
    const stepOfCopy = wizardCopy.stepOf
      .replace("{{current}}", displayCurrent)
      .replace("{{total}}", totalSteps)

    const backButton = (small) => (
      <Button
        variant="link"
        small={small}
        icon="fa-chevron-left"
        iconStyle="solid"
        disabled={isSubmitting}
        onClick={handleBack}
      >
        {wizardCopy.back}
      </Button>
    )

    return (
      <div className="flex flex-col">
        <div
          className="hidden items-start gap-x-6 lg:flex"
          data-testid="wizard-nav-desktop"
        >
          {backButton(false)}
          <div className="flex flex-1 gap-x-3">
            {stepperSteps.map((step, index) => {
              const stepNumber = index + 2
              const displayNumber = index + 1
              const current = !declinedOrExpired && currentStep === stepNumber
              const completed =
                declinedOrExpired || (step.validate(state) && !current)

              return (
                <button
                  key={step.key}
                  type="button"
                  className={classnames(
                    "min-w-0 flex-1 space-y-2 bg-transparent text-left",
                    isNavigable(stepNumber)
                      ? "cursor-pointer"
                      : "cursor-not-allowed"
                  )}
                  onClick={handleClickStep(stepNumber)}
                >
                  <span
                    className={classnames(
                      "block truncate text-center text-xs",
                      {
                        "text-teal-600": completed,
                        "font-semibold text-blue-700": current,
                        "text-gray-400": !completed && !current,
                      }
                    )}
                  >
                    {completed && <i className="fa-solid fa-check mr-1" />}
                    {current ? `${displayNumber}. ${step.title}` : step.title}
                  </span>
                  <span
                    className={classnames("block h-1 w-full rounded", {
                      "bg-teal-600": completed,
                      "bg-blue-700": current,
                      "bg-gray-400": !completed && !current,
                    })}
                  />
                </button>
              )
            })}
          </div>
        </div>

        <div className="lg:hidden" data-testid="wizard-nav-mobile">
          <div className="flex items-center justify-between">
            {backButton(true)}
            <span className="text-xs font-semibold uppercase text-gray-900">
              {stepOfCopy}
            </span>
          </div>
          <div className="mt-2 flex space-x-1">
            {stepperSteps.map((step, index) => {
              const stepNumber = index + 2
              const current = !declinedOrExpired && currentStep === stepNumber
              const completed =
                declinedOrExpired || (step.validate(state) && !current)

              return (
                <span
                  key={step.key}
                  className={classnames("h-1 flex-1 rounded", {
                    "bg-blue-700": completed || current,
                    "bg-gray-400": !completed && !current,
                  })}
                />
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex w-full items-center justify-between rounded-lg border border-gray-300 p-4">
      {steps.map((step, index) => {
        const stepNumber = index + 1

        return (
          <Fragment key={step.key}>
            <button
              className={`flex items-center space-x-2 bg-transparent ${
                isNavigable(stepNumber)
                  ? "cursor-pointer"
                  : "cursor-not-allowed"
              }`}
              onClick={handleClickStep(stepNumber)}
            >
              <StepNumber
                number={stepNumber}
                active={declinedOrExpired || currentStep >= stepNumber}
                complete={declinedOrExpired || step.validate(state)}
              />
              <span className="hidden text-center font-semibold lg:inline">
                {step.title}
              </span>
            </button>
            <div className="flex shrink grow items-center justify-center last:hidden">
              <Transition
                show={declinedOrExpired || currentStep > stepNumber}
                className="flex w-full items-center justify-center"
                enter="transition-opacity duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <span className="inline-block h-1 w-3/4 rounded-lg bg-gray-400" />
              </Transition>
            </div>
          </Fragment>
        )
      })}
    </div>
  )
}

SignContractWizardNavigation.propTypes = {
  isSubmitting: PropTypes.bool.isRequired,
}

export default SignContractWizardNavigation
